import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, ArrowLeft, ArrowRight, PaperPlaneTilt, EnvelopeSimple, Signature } from "@phosphor-icons/react";

// Assistant « Signatures une par une » : aperçu du courriel réel, confirmation, envoi, suivant.
export default function SignatureWizard({ onClose, onSent }) {
  const [queue, setQueue] = useState(null);
  const [meta, setMeta] = useState({});
  const [idx, setIdx] = useState(0);
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sentIds, setSentIds] = useState([]);

  useEffect(() => {
    api.get("/student-contracts/signature-queue").then((r) => { setQueue(r.data.queue); setMeta(r.data); })
      .catch(() => toast.error("Impossible de charger la file de signatures."));
  }, []);

  const current = queue?.[idx];
  const loadPreview = useCallback(async (c) => {
    setPreview(null); setConfirming(false);
    if (!c) return;
    try {
      const r = await api.get(`/student-contracts/${c.id}/contract-email-preview`, { params: { origin_url: window.location.origin } });
      setPreview(r.data);
    } catch { toast.error("Aperçu indisponible."); }
  }, []);
  useEffect(() => { loadPreview(current); }, [current, loadPreview]);

  const go = (d) => setIdx((i) => Math.min(Math.max(i + d, 0), (queue?.length || 1) - 1));

  const send = async () => {
    if (!current || !preview?.to_email) return;
    setBusy(true);
    try {
      const r = await api.post(`/student-contracts/${current.id}/send-contract-and-payment`, { origin_url: window.location.origin });
      toast.success(`Envoyé à ${r.data.email} ✓`);
      setSentIds((s) => [...s, current.id]);
      setQueue((q) => q.map((x) => x.id === current.id ? { ...x, sign_state: "attente", contract_sent_at: new Date().toISOString() } : x));
      onSent?.();
      if (idx < queue.length - 1) setIdx(idx + 1); else setConfirming(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible."); }
    finally { setBusy(false); setConfirming(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="signature-wizard">
      <div className="bg-white text-zinc-900 w-full max-w-5xl max-h-[92vh] flex flex-col hard-border shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b bg-zinc-950 text-white">
          <div className="flex items-center gap-2">
            <Signature size={20} weight="duotone" className="text-amber-400" />
            <div>
              <div className="font-bold text-sm uppercase tracking-wide">Signatures une par une</div>
              <div className="text-[11px] text-zinc-400" data-testid="wizard-counter">
                {queue ? `${idx + 1} / ${queue.length} à signer · ${meta.signed || 0} déjà signés · ${sentIds.length} envoyé(s) cette séance` : "Chargement…"}
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="wizard-close" className="p-1.5 hover:bg-white/10 rounded"><X size={18} /></button>
        </div>

        {queue && queue.length === 0 && (
          <div className="p-10 text-center text-zinc-600" data-testid="wizard-empty">🎉 Tous les contrats actifs sont signés.</div>
        )}

        {current && (
          <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr]">
            <aside className="border-r p-5 space-y-4 overflow-y-auto bg-zinc-50">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Élève</div>
                <div className="text-xl font-black" data-testid="wizard-member-name">{current.full_name}</div>
                <div className="text-sm text-zinc-600 capitalize">{(current.discipline || "").replace(/_/g, " ")}{current.age ? ` · ${current.age} ans` : ""}</div>
                {current.tarif_mensuel != null && <div className="text-sm text-zinc-600">{current.tarif_mensuel} $ / 4 sem.</div>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Destinataire</div>
                {current.to_email
                  ? <div className="text-sm font-mono break-all flex items-center gap-1.5" data-testid="wizard-member-email"><EnvelopeSimple size={14} /> {current.to_email}</div>
                  : <div className="text-sm text-red-600 font-semibold" data-testid="wizard-no-email">Aucun courriel — ajoute-le à la fiche d'abord.</div>}
                {current.parent_name && <div className="text-xs text-zinc-500 mt-1">Parent : {current.parent_name}</div>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">État</div>
                <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${current.sign_state === "attente" ? "bg-amber-100 text-amber-800" : "bg-zinc-200 text-zinc-700"}`} data-testid="wizard-member-state">
                  {current.sign_state === "attente" ? `⏳ Envoyé le ${(current.contract_sent_at || "").slice(0, 10)} — relance` : "Jamais envoyé"}
                </span>
              </div>
              <ol className="text-[11px] text-zinc-500 space-y-1 pt-2 border-t max-h-56 overflow-y-auto" data-testid="wizard-queue-list">
                {queue.map((q, i) => (
                  <li key={q.id} className={`flex items-center gap-1.5 cursor-pointer ${i === idx ? "text-zinc-900 font-semibold" : ""}`} onClick={() => setIdx(i)}>
                    <span className={`w-1.5 h-1.5 rounded-full ${sentIds.includes(q.id) ? "bg-emerald-500" : q.sign_state === "attente" ? "bg-amber-400" : "bg-zinc-300"}`} />
                    {q.full_name}
                  </li>
                ))}
              </ol>
            </aside>

            <main className="p-5 overflow-y-auto flex flex-col gap-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Aperçu du courriel qui sera envoyé</div>
              {preview ? (
                <>
                  <div className="text-sm"><span className="text-zinc-500">Objet :</span> <b data-testid="wizard-subject">{preview.subject}</b></div>
                  <iframe title="aperçu" srcDoc={preview.html} className="w-full flex-1 min-h-[320px] border rounded bg-[#0A0A0A]" data-testid="wizard-preview-frame" sandbox="" />
                  <div className="text-[11px] text-zinc-500 break-all">Lien de signature : {preview.sign_url}</div>
                </>
              ) : <div className="text-sm text-zinc-500">Chargement de l'aperçu…</div>}
            </main>
          </div>
        )}

        {current && (
          <div className="border-t px-5 py-3 flex flex-wrap items-center justify-between gap-2 bg-white">
            <div className="flex gap-2">
              <button onClick={() => go(-1)} disabled={idx === 0} data-testid="wizard-prev" className="hard-border px-3 py-2 text-sm flex items-center gap-1 disabled:opacity-40"><ArrowLeft size={14} /> Précédent</button>
              <button onClick={() => go(1)} disabled={idx >= queue.length - 1} data-testid="wizard-skip" className="hard-border px-3 py-2 text-sm flex items-center gap-1 disabled:opacity-40">Passer <ArrowRight size={14} /></button>
            </div>
            {!confirming ? (
              <button onClick={() => setConfirming(true)} disabled={!preview?.to_email || busy} data-testid="wizard-send"
                className="bg-[#D90429] text-white px-5 py-2 text-sm font-bold uppercase tracking-wide flex items-center gap-2 disabled:opacity-40">
                <PaperPlaneTilt size={16} weight="fill" /> Envoyer à {current.full_name?.split(" ")[0]}
              </button>
            ) : (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 px-3 py-2" data-testid="wizard-confirm-box">
                <span className="text-sm text-amber-900">Envoi RÉEL à <b>{preview?.to_email}</b> — confirmer ?</span>
                <button onClick={() => setConfirming(false)} className="text-sm px-3 py-1 hard-border bg-white" data-testid="wizard-confirm-cancel">Annuler</button>
                <button onClick={send} disabled={busy} className="text-sm px-3 py-1 bg-emerald-600 text-white font-bold" data-testid="wizard-confirm-send">{busy ? "Envoi…" : "Oui, envoyer"}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
